import { TitleButtonDivider } from '@/common/buttons/TitleButtonDivider';
import { EntityPerspective } from '@/common/entityFramework/entityPerspective/EntityPerspective';
import { useFilterButtons } from '@/common/entityFramework/entityPerspective/hooks/useFilterButtons';
import { RowIcon } from '@/common/entityFramework/entityPerspective/support/RowIcon';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useCallback, useRef } from 'react';
import { TheraSelect } from '../../theraSelect/TheraSelect';

export const AbvPbmPymtPerspective = () => {
  const entityPerspectiveRef = useRef();
  const theraSelectRef = useRef();

  const views = [
    {
      name: 'Payments',
      dims: ['PAID_PERIOD', 'CHANNEL', 'PBM', 'PROD', 'EARNED_PERIOD'],
      kpis: ['PYMT_AMT', 'PYMT_ADJ', 'TOTAL_PYMT'],
      dimToPeriodicities: {
        PAID_PERIOD: ['LIFETIME', 'YEAR', 'QTR', 'MON'],
        EARNED_PERIOD: ['YEAR', 'QTR'],
      },
    },
  ];

  const { filterButtonStages, filterButtons, onViewReset } = useFilterButtons([
    {
      key: 'uncategorized',
      icon: fas.faExclamationTriangle,
      colors: [null, 'red', 'green'],
      toolTips: ['Uncategorized and categorized payments', 'Uncategorized payments', 'Categorized payments'],
    },
  ]);

  const handleViewReset = useCallback(() => {
    onViewReset();
    theraSelectRef.current.reset();
  }, [onViewReset]);

  const augButtons = (buttons) => {
    buttons.push(
      <TheraSelect entityPerspectiveRef={entityPerspectiveRef} ref={theraSelectRef} />,
      ...filterButtons,
      <TitleButtonDivider key="filterButtonDivider" />,
    );
  };

  const augReqParam = useCallback(
    (reqParam) => {
      // Uncategorized
      const uncategorizedButtonStage = filterButtonStages.uncategorized;
      if (uncategorizedButtonStage > 0) {
        reqParam.expr = ExprUtils.and(
          reqParam.expr,
          uncategorizedButtonStage === 1 ?
            ExprUtils.whereParams('uncategorized', 1)
          : ExprUtils.isNot('uncategorized', 1),
        );
      }
    },
    [filterButtonStages],
  );

  const rowIconFn = (data) => {
    let rowIcons = [];

    // Uncategorized row icon
    if (data.uncategorized === 1) {
      rowIcons.push(
        <RowIcon key="uncategorized" icon={fas.faExclamationTriangle} color="red" toolTip="Uncategorized payment" />,
      );
    } else if (data.uncategorized > 0) {
      rowIcons.push(
        <RowIcon
          key="containsUncategorized"
          icon={fas.faExclamationTriangle}
          color="pink"
          toolTip="Contains an uncategorized payment"
        />,
      );
    }

    return rowIcons;
  };

  const getContextMenuItems = (params) => {
    let contextMenus = entityPerspectiveRef.current.getContextMenuItems(params);

    let pymtSubMenu = [
      {
        name: 'Payment Transactions',
        action: () =>
          entityPerspectiveRef.current.navigateToViewer(
            params,
            'PYMT_AMT',
            'Payment Transactions',
            'PBM_PYMT_VIEWER_PLUGIN',
          ),
        icon: fas.faListUl,
      },
      {
        name: 'Pivot Payment Transactions',
        action: () => entityPerspectiveRef.current.navigateToPivot(params, 'PYMT_AMT', 'Payment Transactions'),
        icon: fas.faTableColumns,
      },
    ];

    let pymtAdjSubMenu = [
      {
        name: 'Payment Adjustment Transactions',
        action: () =>
          entityPerspectiveRef.current.navigateToViewer(params, 'PYMT_ADJ', 'Payment Adjustment Transactions'),
        icon: fas.faListUl,
      },
      {
        name: 'Pivot Payment Transactions',
        action: () =>
          entityPerspectiveRef.current.navigateToPivot(params, 'PYMT_AMT', 'Payment Adjustment Transactions'),
        icon: fas.faTableColumns,
      },
    ];

    contextMenus.push(
      {
        name: 'Payment Amounts',
        subMenu: pymtSubMenu,
        icon: fas.faListUl,
      },
      {
        name: 'Payment Adjustments',
        subMenu: pymtAdjSubMenu,
        icon: fas.faListUl,
      },
      {
        name: 'Export',
        action: () => entityPerspectiveRef.current.openExportDialog(params),
        icon: fas.faArrowCircleDown,
      },
    );

    return contextMenus;
  };

  const events = {
    onDimFilterChange: ({ dimCode }) => {
      if (dimCode === 'PROD') {
        theraSelectRef.current.reset();
      }
    },
  };

  return (
    <EntityPerspective
      ref={entityPerspectiveRef}
      views={views}
      rowIconsFn={rowIconFn}
      augButtons={augButtons}
      onViewReset={handleViewReset}
      events={events}
      augReqParam={augReqParam}
      gridOptions={{
        getContextMenuItems,
      }}
    />
  );
};
